import React, { ReactElement, forwardRef } from 'react'
import { Box, Link as MuiLink } from '@mui/material'
import { Link as GatsbyLink } from 'gatsby'
import clsx from 'clsx'
import Icon from '@components/core/media/Icon'

export type LinkProps = {
  id?: string
  to?: string
  underline?: 'none' | 'always' | 'hover'
  variant?: 'body1' | 'navigation'
  isDownload?: boolean
  isExternal?: boolean
  isActive?: boolean
  onClick?: (ev?: any) => void
  onMouseOver?: () => void
  onMouseOut?: () => void
  children?: MCDC.Props.IDefault['children']
  sx?: MCDC.Props.IDefault['sx']
  ariaLabel?: string
  title?: string
}

export default forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  {
    id,
    children,
    to,
    isExternal = false,
    isDownload = false,
    isActive = false,
    underline = 'hover',
    variant = 'body1',
    onClick,
    onMouseOver,
    onMouseOut,
    sx,
    ariaLabel,
    title,
  },
  ref
) {
  const className = clsx({
    ['Mui-active']: isActive,
  })

  const LinkComponent =
    !isExternal && to ? GatsbyLink : !to && onClick ? 'button' : 'a'
  const additionalProps =
    isExternal || isDownload
      ? { href: to, target: '_blank', rel: 'noreferrer', download: isDownload }
      : { to }

  const isExternalUrl =
    additionalProps.href && additionalProps.href.startsWith('http')

  return (
    <MuiLink
      {...additionalProps}
      id={id}
      className={className}
      variant={variant}
      underline={underline}
      component={LinkComponent}
      onClick={onClick}
      onMouseOver={onMouseOver}
      onMouseOut={onMouseOut}
      sx={sx}
      aria-label={ariaLabel}
      title={title}
      ref={ref}
    >
      <Box
        component="span"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}
      >
        {children}
        {isExternalUrl && (
          <Box
            component="span"
            sx={{ display: 'block', width: '16px', height: '16px' }}
          >
            <Icon variant="external" size="small" />
          </Box>
        )}
      </Box>
    </MuiLink>
  )
})
